/**
 * Scene Preview Generation Service
 * 
 * Generates cached preview images for scene/display configurations.
 * Instead of recalculating layer inheritance on every preview load,
 * we generate a 400px screenshot once when the scene is edited.
 */

import { getDataProvider } from '@/lib/dataProvider'
import { useAuthStore } from '@/stores/auth'
import { useDisplayLayersStore } from '@/stores/displayLayers'
import { useSceneDisplayConfigsStore } from '@/stores/sceneDisplayConfigs'
import { useScenesStore } from '@/stores/scenes'
import { useImageLibraryStore } from '@/stores/imageLibrary'
import { useImageCacheStore } from '@/stores/imageCache'
import type { DisplayLayer } from '@/stores/sceneDisplayConfigs'
import type { Display } from '@/stores/displays'
import { logger } from '@/lib/logger'

const PREVIEW_MAX_SIZE = 400 // Max width or height in pixels

interface PreviewGenerationResult {
  success: boolean
  storagePath?: string
  error?: string
}

/**
 * Load an image and return it as HTMLImageElement
 */
const loadImage = (src: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error(`Failed to load image: ${src}`))
    img.src = src
  })
}

/**
 * Map CSS blend mode to canvas globalCompositeOperation
 */
const blendModeToComposite = (blendMode: string): GlobalCompositeOperation => {
  const mapping: Record<string, GlobalCompositeOperation> = {
    'normal': 'source-over',
    'multiply': 'multiply',
    'screen': 'screen',
    'overlay': 'overlay',
    'darken': 'darken',
    'lighten': 'lighten',
    'color-dodge': 'color-dodge',
    'color-burn': 'color-burn',
    'hard-light': 'hard-light',
    'soft-light': 'soft-light',
    'difference': 'difference',
    'exclusion': 'exclusion',
    'hue': 'hue',
    'saturation': 'saturation',
    'color': 'color',
    'luminosity': 'luminosity',
  }
  return mapping[blendMode] || 'source-over'
}

/**
 * Render layers to a canvas and return as Blob
 */
const renderLayersToCanvas = async (
  layers: DisplayLayer[],
  display: Display,
  imageLibrary: Map<string, { storage_path?: string; preview_storage_path?: string }>,
  imageCacheStore: ReturnType<typeof useImageCacheStore>
): Promise<Blob> => {
  // Calculate scale to fit within PREVIEW_MAX_SIZE
  const scale = Math.min(
    PREVIEW_MAX_SIZE / display.width,
    PREVIEW_MAX_SIZE / display.height,
    1
  )
  
  const width = Math.round(display.width * scale)
  const height = Math.round(display.height * scale)
  
  // Create canvas
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const ctx = canvas.getContext('2d')!
  
  // Fill with black background
  ctx.fillStyle = '#000000'
  ctx.fillRect(0, 0, width, height)
  
  // Sort layers by z-index (lower zIndex = rendered first = behind)
  const sortedLayers = [...layers]
    .filter(l => l.visible !== false)
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0))
  
  logger.info(`[PreviewService] Rendering ${sortedLayers.length} visible layers`)
  
  // Render each layer
  for (const layer of sortedLayers) {
    if (layer.type === 'image') {
      try {
        // Get image URL from cache using storage path - prefer preview for efficiency
        const imageInfo = imageLibrary.get(layer.content_source)
        // Use preview_storage_path if available, fall back to full storage_path
        const storagePath = imageInfo?.preview_storage_path || imageInfo?.storage_path || layer.storage_path
        
        let imageUrl = ''
        if (storagePath) {
          // Use cache to get the URL (this will fetch if needed)
          imageUrl = await imageCacheStore.getImageByPath(storagePath, 'user-images')
        }
        
        if (!imageUrl) {
          logger.warn(`[PreviewService] No image URL for layer ${layer.id} (content_source: ${layer.content_source}, storage_path: ${layer.storage_path}), skipping`)
          continue
        }
        
        const img = await loadImage(imageUrl)
        
        // Calculate scaled position and size
        const x = Math.round((layer.x ?? layer.position_x ?? 0) * scale)
        const y = Math.round((layer.y ?? layer.position_y ?? 0) * scale)
        const w = Math.round(layer.width * scale)
        const h = Math.round(layer.height * scale)
        
        // Apply effects
        ctx.save()
        
        // Apply CSS-like filters using canvas filter property
        // Note: ctx.filter has good browser support now
        const filters: string[] = []
        if (layer.effects?.blur !== undefined && layer.effects.blur > 0) {
          filters.push(`blur(${layer.effects.blur * scale}px)`)
        }
        if (layer.effects?.brightness !== undefined && layer.effects.brightness !== 1) {
          filters.push(`brightness(${layer.effects.brightness})`)
        }
        if (layer.effects?.contrast !== undefined && layer.effects.contrast !== 1) {
          filters.push(`contrast(${layer.effects.contrast})`)
        }
        if (layer.effects?.saturation !== undefined && layer.effects.saturation !== 1) {
          filters.push(`saturate(${layer.effects.saturation})`)
        }
        if (layer.effects?.hue !== undefined && layer.effects.hue !== 0) {
          filters.push(`hue-rotate(${layer.effects.hue}deg)`)
        }
        if (filters.length > 0) {
          ctx.filter = filters.join(' ')
        }
        
        // Apply blend mode
        if (layer.effects?.blendMode && layer.effects.blendMode !== 'normal') {
          ctx.globalCompositeOperation = blendModeToComposite(layer.effects.blendMode)
        }
        
        // Apply opacity
        if (layer.effects?.opacity !== undefined && layer.effects.opacity !== 1) {
          ctx.globalAlpha = layer.effects.opacity
        }
        
        // Apply rotation
        if (layer.effects?.rotation) {
          ctx.translate(x + w / 2, y + h / 2)
          ctx.rotate((layer.effects.rotation * Math.PI) / 180)
          ctx.translate(-(x + w / 2), -(y + h / 2))
        }
        
        // Draw image with crop support
        const crop = layer.crop || layer.crop_settings
        if (crop && (crop.top || crop.left || crop.right || crop.bottom)) {
          // Calculate crop in source image coordinates
          const srcX = (crop.left / 100) * img.width
          const srcY = (crop.top / 100) * img.height
          const srcW = img.width * (1 - (crop.left + crop.right) / 100)
          const srcH = img.height * (1 - (crop.top + crop.bottom) / 100)
          
          ctx.drawImage(img, srcX, srcY, srcW, srcH, x, y, w, h)
        } else {
          ctx.drawImage(img, x, y, w, h)
        }
        
        // Apply tint overlay
        if (layer.effects?.tint) {
          // Reset composite for tint
          ctx.globalCompositeOperation = 'multiply'
          ctx.globalAlpha = 0.5
          ctx.fillStyle = layer.effects.tint
          ctx.fillRect(x, y, w, h)
        }
        
        ctx.restore()
        
        logger.debug(`[PreviewService] Rendered layer ${layer.id} (${layer.name || layer.type}) at ${x},${y} size ${w}x${h}`)
      } catch (err) {
        logger.warn(`[PreviewService] Failed to render layer ${layer.id}:`, err)
      }
    } else if (layer.type === 'youtube') {
      // For YouTube, draw a placeholder with the thumbnail
      const x = Math.round((layer.x || layer.position_x || 0) * scale)
      const y = Math.round((layer.y || layer.position_y || 0) * scale)
      const w = Math.round(layer.width * scale)
      const h = Math.round(layer.height * scale)
      
      try {
        // Extract video ID and get thumbnail
        const videoIdMatch = layer.content?.match(/embed\/([a-zA-Z0-9_-]+)/)
        if (videoIdMatch) {
          const thumbnailUrl = `https://img.youtube.com/vi/${videoIdMatch[1]}/hqdefault.jpg`
          const img = await loadImage(thumbnailUrl)
          ctx.drawImage(img, x, y, w, h)
        }
      } catch {
        // Draw placeholder if thumbnail fails
        ctx.fillStyle = '#1a1a1a'
        ctx.fillRect(x, y, w, h)
        ctx.fillStyle = '#ff0000'
        ctx.font = `${Math.round(h * 0.3)}px Arial`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText('▶', x + w / 2, y + h / 2)
      }
    } else if (layer.type === 'text') {
      const x = Math.round((layer.x || layer.position_x || 0) * scale)
      const y = Math.round((layer.y || layer.position_y || 0) * scale)
      const w = Math.round(layer.width * scale)
      const h = Math.round(layer.height * scale)
      
      // Draw text background
      ctx.fillStyle = 'rgba(59, 130, 246, 0.5)' // Blue-ish
      ctx.fillRect(x, y, w, h)
      
      // Draw text
      ctx.fillStyle = '#1e40af'
      ctx.font = `${Math.round(h * 0.4)}px Arial`
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      const text = layer.content || 'Text'
      ctx.fillText(text.slice(0, 20), x + w / 2, y + h / 2)
    }
  }
  
  // Convert to blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => blob ? resolve(blob) : reject(new Error('Failed to create blob')),
      'image/jpeg',
      0.85
    )
  })
}

/**
 * Generate preview for a specific scene/display config
 */
export const generateScenePreview = async (
  sceneId: string,
  displayId: string,
  display: Display
): Promise<PreviewGenerationResult> => {
  const authStore = useAuthStore()
  const layersStore = useDisplayLayersStore()
  const configsStore = useSceneDisplayConfigsStore()
  const imageLibraryStore = useImageLibraryStore()
  
  if (!authStore.user?.id) {
    return { success: false, error: 'Not authenticated' }
  }
  
  try {
    logger.info(`[PreviewService] Generating preview for scene ${sceneId}, display ${displayId}`)
    
    // Ensure configs are loaded for this scene
    await configsStore.fetchResolvedConfigsForScene(sceneId)
    
    // Get the config
    const config = configsStore.getConfigForSceneAndDisplay(sceneId, displayId)
    if (!config) {
      logger.warn(`[PreviewService] No config found for scene ${sceneId}, display ${displayId}`)
      return { success: false, error: 'No config found' }
    }
    
    // Ensure image library is loaded
    if (imageLibraryStore.images.length === 0) {
      await imageLibraryStore.fetchImages()
    }
    
    // Fetch layers with inheritance (this is the expensive operation we're caching!)
    const layers = await layersStore.fetchLayersWithInheritance(config.id, sceneId, displayId)
    
    logger.info(`[PreviewService] Fetched ${layers.length} layers for scene ${sceneId}`)
    
    if (layers.length === 0) {
      logger.info(`[PreviewService] No layers to render for scene ${sceneId}`)
      // Clear any existing preview
      if (config.preview_storage_path) {
        await updateConfigPreviewPath(config.id, null)
      }
      return { success: true, storagePath: undefined }
    }
    
    // Build image library map for quick lookups (prefer preview paths)
    const imageMap = new Map<string, { storage_path?: string; preview_storage_path?: string }>()
    imageLibraryStore.images.forEach(img => {
      imageMap.set(img.id, { 
        storage_path: img.storage_path,
        preview_storage_path: img.preview_storage_path ?? undefined
      })
    })
    
    // Get image cache store for URL resolution
    const imageCacheStore = useImageCacheStore()
    
    // Render to canvas
    const blob = await renderLayersToCanvas(layers, display, imageMap, imageCacheStore)
    
    // Upload to storage
    const provider = getDataProvider()
    const fileName = `preview_${config.id}_${Date.now()}.jpg`
    const storagePath = `${authStore.user.id}/previews/${fileName}`
    
    const uploadResult = await provider.uploadFile(
      'user-images',
      storagePath,
      new File([blob], fileName, { type: 'image/jpeg' })
    )
    
    if (!uploadResult.success) {
      return { success: false, error: uploadResult.error || 'Upload failed' }
    }
    
    // Delete old preview if exists
    if (config.preview_storage_path) {
      try {
        await provider.deleteFile('user-images', config.preview_storage_path)
      } catch (err) {
        logger.warn(`[PreviewService] Failed to delete old preview:`, err)
      }
    }
    
    // Update config with new preview path
    await updateConfigPreviewPath(config.id, storagePath)
    
    logger.info(`[PreviewService] Preview generated: ${storagePath}`)
    return { success: true, storagePath }
    
  } catch (err) {
    logger.error(`[PreviewService] Failed to generate preview:`, err)
    return { success: false, error: err instanceof Error ? err.message : 'Unknown error' }
  }
}

/**
 * Update the preview_storage_path in the config
 */
const updateConfigPreviewPath = async (configId: string, storagePath: string | null) => {
  const provider = getDataProvider()
  await provider.update('scene_display_configs', configId, {
    preview_storage_path: storagePath
  })
  
  // Update local store
  const configsStore = useSceneDisplayConfigsStore()
  const existingConfig = configsStore.configs.get(configId)
  if (existingConfig) {
    configsStore.configs.set(configId, {
      ...existingConfig,
      preview_storage_path: storagePath
    })
  }
}

/**
 * Regenerate previews for a scene and all its children
 * Call this when layers are modified
 */
export const regeneratePreviewsForSceneTree = async (
  sceneId: string,
  displayId: string,
  display: Display
): Promise<void> => {
  const scenesStore = useScenesStore()
  
  // Generate for the current scene
  await generateScenePreview(sceneId, displayId, display)
  
  // Find all child scenes and regenerate their previews
  // (because they inherit from this scene)
  const childSceneIds = getChildSceneIds(sceneId, scenesStore.scenes)
  
  for (const childId of childSceneIds) {
    await generateScenePreview(childId, displayId, display)
  }
}

/**
 * Get all descendant scene IDs for a given parent
 */
const getChildSceneIds = (parentId: string, scenes: Map<string, any>): string[] => {
  const children: string[] = []
  
  scenes.forEach((scene, id) => {
    if (scene.parent_id === parentId) {
      children.push(id)
      // Recursively get children of children
      children.push(...getChildSceneIds(id, scenes))
    }
  })
  
  return children
}

/**
 * Check if a preview needs to be generated (missing or stale)
 */
export const needsPreviewGeneration = (previewPath: string | null | undefined): boolean => {
  return !previewPath
}
